import {Alert, Button, Col, DatePicker, Divider, Icon, Input, Layout, Radio, Row, Spin, Tooltip} from 'antd';
import { castArray } from 'common/util';
import NewFlightDrawer from 'components/flights/scheduling/NewFlightDrawer';
import MainHeader from 'components/scheduling/MainHeader';
import MainLayout from 'components/scheduling/MainLayout';
import WithOrgData, { WithOrgDataProps } from 'components/WithOrgData';
import gql from 'graphql-tag';
import moment from 'moment';
import React, { useState } from 'react';
import { useApolloClient, useQuery } from 'react-apollo';
import MonthSelect from '../calendar/monthselect';
import { ScheduledFlight } from '../interface';
import { usePersistentDateRange } from '../util';
import ScheduledFlightsCalendar from './calendar';
import GroupList from './list';
import useUserGroups from "../../../hooks/useUserGroups";
export interface ScheduledFlightsListProps extends WithOrgDataProps {
    onFlightClicked?(flight: ScheduledFlight): void,
    onNewFlightSubmitted?(flightId: string): void
}

const { Content } = Layout;

const { RangePicker } = DatePicker;

const dividerStyle = {
    height: '3rem',
    margin: '0 1rem',
    background: '#dcdcdc'
}

const GET_FLIGHTS = gql`
query GetFlights(
    $tpID: ID!
    $customerID: ID!
    $startDate: AWSDate!
    $endDate: AWSDate!
){
    getScheduledFlights(
        tpID: $tpID
        customerID: $customerID
        startDate: $startDate
        endDate: $endDate
    ){
        _id
        createdBy
        createdTs
        modifiedTs
        modifiedBy
        aircraftID {
            _id
            tailNum
        }
        scheduledFlightDate
        desig
        legsArray {
            departureID
            departure
            destinationID
            destination
        }
    }
}
`

function formatDate(date: moment.Moment){
    return date.format('YYYY-MM-DD')
}

const ScheduledFlightsList: React.FC<ScheduledFlightsListProps> = (props) => {
    const [ calValue, setCalValue ] = useState(moment());
    const [ listSearch, setListSearch ] = useState<string>('');
    const [ newFlightOpen, setNewFlightOpen ] = useState(false);

    const [ userGroups ] = useUserGroups();
    const client = useApolloClient();
    var { data: viewData } = useQuery(gql`
    query GetCalendarView {
        schedulingCalendarView @client
    }
    `)

    const [ dateRange, setDateRange ] = usePersistentDateRange();

    const view = (viewData && viewData.schedulingCalendarView) || '0'

    function setView(value: string){
        client.writeData({
            data: {
                schedulingCalendarView: value
            }
        })
    }

    if (view === '1'){
        var startDate = formatDate(calValue.clone().startOf('month'));
        var endDate = formatDate(calValue.clone().endOf('month'));
    }
    else
    {
        startDate = formatDate(dateRange[0]);
        endDate = formatDate(dateRange[1]);
    }

    const { loading, error, data: queryData } = useQuery(GET_FLIGHTS, {
        fetchPolicy: 'network-only',
        pollInterval: 5000,
        variables: {
            tpID: props.orgData.getOrgIDByType('transporter'),
            customerID: props.orgData.getOrgIDByType('customer'),
            startDate,
            endDate
        }

    })

    let data: Array<ScheduledFlight> = castArray(queryData && queryData.getScheduledFlights);

    if (view === '0' && listSearch.length > 0){
        data = data.filter((item) => item.desig.toLowerCase().includes(listSearch.toLowerCase().trim()))
     }

    let content: React.ReactElement;

    let viewActions: React.ReactElement;

    switch (view) {
        case '0':
            viewActions = <Row type="flex" gutter={12}>
                <Col style={{ maxWidth: '16rem' }}>
                    <RangePicker
                        value={dateRange}
                        onChange={(dates: [moment.Moment, moment.Moment]) => setDateRange(dates)}
                        format="MMM DD, YY"
                        disabledDate={(current) => moment().isAfter(current, 'month')}
                    />
                </Col>
                <Col style={{ flex: 1 }}>
                    <Input
                        placeholder="Search flights"
                        prefix={<Icon type="search" style={{ color: 'rgba(0,0,0,.25)' }} />}
                        value={listSearch}
                        onChange={(e) => setListSearch(e.target.value)}
                        allowClear
                    />
                </Col>
            </Row>
            content = <div style={{ padding: '0 1rem' }}>
                <GroupList data={data} onFlightClicked={(flight) => props.onFlightClicked && props.onFlightClicked(flight)} />
            </div>
            break;
        case '1':
        default:
            viewActions = <Row type="flex" gutter={12}>
                <Col>
                    <Button block onClick={() => setCalValue(moment())}>This Month</Button>
                </Col>
                <Col style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                    <MonthSelect
                        style={{ width: '100%' }}
                        value={calValue}
                        onChange={(value) => setCalValue(value)}
                    />
                </Col>
            </Row>
            content = <ScheduledFlightsCalendar
                data={data}
                value={calValue}
                onFlightClicked={(flight) => props.onFlightClicked && props.onFlightClicked(flight)}
            />
            break;
    }

    return <MainLayout>
        <MainHeader
            routes={[
                {
                    path: 'app/scheduling/flights',
                    breadcrumbName: 'Scheduled Flights'
                }
            ]}
            actions={
                <Row type="flex" gutter={12} align="middle">
                    <Col>
                        <Radio.Group
                            buttonStyle="solid"
                            onChange={(e) => setView(e.target.value)}
                            value={view}
                        >
                            <Radio.Button value="0"><Icon type="unordered-list" style={{ marginRight: 6 }} />List</Radio.Button>
                            <Radio.Button value="1"><Icon type="calendar" style={{ marginRight: 6 }} />Calendar</Radio.Button>
                        </Radio.Group>
                    </Col>
                    <Divider type="vertical" style={dividerStyle} />
                    <Col style={{ flex: 1, width: '28rem' }}>
                        {viewActions}
                    </Col>
                    <Divider type="vertical" style={dividerStyle} />
                    <Col>
                        {userGroups.includes('flytsuite.schedule.flight') ? (
                            <Button
                                type="primary"
                                onClick={() => setNewFlightOpen(true)}
                            >New Flight</Button>
                        ) :
                        (
                            <Tooltip title="You do not have permission to access this feature.">
                                <Button
                                    type="primary"
                                    disabled={true}
                                >New Flight</Button>
                            </Tooltip>
                        )}
                    </Col>
                </Row>
            }
        />
        <Content style={{overflow: 'auto'}}>
            {error ? <Alert
                message="Failed to load scheduled groups"
                description={error.message}
                type="error"
                showIcon
                banner
            /> : null}
            <div className="ant-spin-fill-height">
                <Spin spinning={loading} size="large" indicator={<Icon type="loading" />}>
                    {content}
                </Spin>
            </div>
        </Content>
        <NewFlightDrawer
            visible={newFlightOpen}
            onClose={() => setNewFlightOpen(false)}
            onCompleted={(data) => props.onNewFlightSubmitted(data._id)}
        />
    </MainLayout>
}

export default WithOrgData(ScheduledFlightsList)